/**
 * Map focus desk. Search returns country, city, district, or road only.
 * The choice is stored for this account and restored after sign-out.
 */
import { useEffect, useState } from "react";
import { html, M, Ico, FADE, stop } from "./ui.js";
import {
  ROAD_CLASSES,
  addPlace,
  emptyFocus,
  enterMapFocus,
  placeFromHit,
  setFocusMode,
  toggleRoadClass,
} from "./ops-map-focus.js";

const LEVEL = { country: "Country", city: "City", district: "District", road: "Road" };

async function searchPlaces(query) {
  const u = new URL("https://nominatim.openstreetmap.org/search");
  u.searchParams.set("format", "jsonv2");
  u.searchParams.set("polygon_geojson", "1");
  u.searchParams.set("addressdetails", "1");
  u.searchParams.set("limit", "6");
  u.searchParams.set("q", query);
  const r = await fetch(u.toString(), { credentials: "omit", headers: { "Accept-Language": "en" } });
  if (!r.ok) throw new Error("search_failed");
  const rows = await r.json();
  const hits = [];
  const seen = new Set();
  for (const row of Array.isArray(rows) ? rows : []) {
    const place = placeFromHit(row);
    if (!place || seen.has(place.id)) continue;
    seen.add(place.id);
    hits.push(place);
  }
  return hits;
}

export function OpsMapFocusDesk({ focus, onChange, onClose, onMiss }) {
  const current = focus || emptyFocus();
  const [q, setQ] = useState("");
  const [hits, setHits] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [asked, setAsked] = useState("");

  useEffect(() => {
    const query = q.trim();
    if (query.length < 2) {
      setHits([]);
      setErr("");
      setBusy(false);
      setAsked("");
      return undefined;
    }
    let stopSearch = false;
    const timer = setTimeout(() => {
      setBusy(true);
      setErr("");
      searchPlaces(query).then((rows) => {
        if (stopSearch) return;
        setHits(rows);
        setAsked(query);
        setBusy(false);
      }).catch(() => {
        if (stopSearch) return;
        setHits([]);
        setAsked(query);
        setBusy(false);
        setErr("Place search did not answer. Try the name again.");
      });
    }, 450);
    return () => {
      stopSearch = true;
      clearTimeout(timer);
    };
  }, [q]);

  useEffect(() => {
    if (typeof onMiss !== "function") return undefined;
    const query = q.trim();
    const failed = !busy && query.length >= 2 && asked === query && !hits.length;
    onMiss(failed ? (err || ("No board matched “" + query + "”.")) : "");
    return undefined;
  }, [busy, q, asked, hits, err, onMiss]);

  useEffect(() => {
    if (typeof onMiss !== "function") return undefined;
    return () => onMiss("");
  }, [onMiss]);

  const picked = new Set(current.places.map((p) => p.id));

  return html`
    <${M.div} className="layers glass ops-map-layers ops-map-focus" data-cmc-map-focus="1"
        initial=${{ x: -14, opacity: 0 }} animate=${{ x: 0, opacity: 1 }}
        exit=${{ x: -10, opacity: 0 }} transition=${FADE} onClick=${stop}>
      <div className="layers__in ops-map-layers__in">
        <div className="ops-map-layers__head">
          <div>
            <p className="eyebrow">Map focus</p>
            <h3 className="ops-map-layers__title">Where this map looks</h3>
          </div>
          <button type="button" className="x" onClick=${onClose} title="Close focus"
            aria-label="Close focus"><${Ico} d="close" w=${13}/></button>
        </div>
        <p className="note">Saved on this browser for this account. Sign-out keeps it. Choosing a place fills the page with that board alone.</p>
        <div className="ops-map-layers__regions" role="group" aria-label="Focus mode">
          <button type="button" className="chip" data-sel=${current.mode === "single" ? "1" : "0"}
            aria-pressed=${current.mode === "single" ? "true" : "false"}
            onClick=${() => onChange(setFocusMode(current, "single"))}>One area</button>
          <button type="button" className="chip" data-sel=${current.mode === "multi" ? "1" : "0"}
            aria-pressed=${current.mode === "multi" ? "true" : "false"}
            onClick=${() => onChange(setFocusMode(current, "multi"))}>Several areas</button>
        </div>
        <label className="ops-map-layers__label" htmlFor="ops-map-focus-q">Country, city, district, or road</label>
        <input id="ops-map-focus-q" className="ops-map-focus__q" type="search" value=${q}
          placeholder="Sibu, Sarawak, or a road name"
          onInput=${(e) => setQ(e.target.value)}/>
        ${busy ? html`<p className="note">Searching…</p>` : null}
        ${err ? html`<p className="note">${err}</p>` : null}
        ${!busy && q.trim().length >= 2 && asked === q.trim() && !hits.length && !err
          ? html`<p className="note">No country, city, district, or road matched.</p>` : null}
        <div className="ops-map-focus__hits" role="list">
          ${hits.map((hit) => html`
            <button key=${hit.id} type="button" className="chip" role="listitem"
              data-sel=${picked.has(hit.id) ? "1" : "0"}
              onClick=${() => onChange(picked.has(hit.id) && !current.exclusive ? enterMapFocus(current) : addPlace(current, hit))}>
              <em>${LEVEL[hit.level] || hit.level}</em> ${hit.name}
            </button>`)}
        </div>
        ${current.places.length ? html`
          <p className="ops-map-layers__label">Chosen</p>
          <div className="ops-map-layers__regions">
            ${current.places.map((place) => html`
              <button key=${place.id} type="button" className="chip" data-sel="1"
                onClick=${() => onChange(addPlace(current, place))}>
                ${place.name} ×
              </button>`)}
            <button type="button" className="chip" onClick=${() => onChange(emptyFocus())}>Clear</button>
          </div>` : null}
        <p className="ops-map-layers__label">Roads <small>· none picked = every road</small></p>
        <div className="ops-map-layers__regions" role="group" aria-label="Road classes">
          ${ROAD_CLASSES.map((row) => {
            const on = current.roads.includes(row.id);
            return html`<button key=${row.id} type="button" className="chip" data-sel=${on ? "1" : "0"}
              aria-pressed=${on ? "true" : "false"}
              onClick=${() => onChange(toggleRoadClass(current, row.id))}>${row.label}</button>`;
          })}
        </div>
      </div>
    <//>`;
}
